import React, { useEffect, useState } from 'react';

import { NOT_SET } from './recruitmentStatusCategories';

import { apiClient } from 'Helpers/network';

import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';

import { useToast } from 'Hooks/useToast';

/**
 * Delete a recruitment status. When positions use it the user must choose where
 * they go: another active status, or Not set. An unused status is deleted as is.
 */
export default function DeleteRecruitmentStatusModal({ modalId, modalRef, status, statuses, deleteUrl, onClose, onSuccess, onFoundInUse }) {
    const [target, setTarget] = useState('');
    const [serverRequiresTarget, setServerRequiresTarget] = useState(false);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toast = useToast();

    useEffect(() => {
        setTarget('');
        setServerRequiresTarget(false);
        setError(null);
    }, [status?.id]);

    const needsTarget = !!status && (status.positions_using > 0 || serverRequiresTarget);
    const targets = statuses.filter((item) => item.is_active && item.id !== status?.id);

    const remove = () => {
        if (needsTarget && target === '') {
            setError($t('Choose the status to move the positions using this status to.'));

            return;
        }

        const data = needsTarget ? { reassign_to: target === NOT_SET ? null : Number(target) } : {};

        setIsSubmitting(true);
        apiClient
            // A 422 is answered in the dialog itself, so it must not raise the crash report.
            .delete(deleteUrl.replace('RECRUITMENT_STATUS_ID', status.id), { data, expectedErrorStatuses: [422] })
            .then(() => {
                toast.success($t('Recruitment status deleted'));
                onSuccess();
            })
            .catch((err) => {
                // The list this dialog was opened from said the status was unused, but
                // positions use it: switch to choosing where they go, with a fresh count.
                if (err.response?.status === 422 && err.response.data?.errors?.reassign_to) {
                    setServerRequiresTarget(true);
                    setError(err.response.data.errors.reassign_to[0]);
                    onFoundInUse?.();

                    return;
                }

                toast.error(err.response?.data?.message || $t('Something went wrong while deleting the recruitment status'));
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <div id={modalId} ref={modalRef} tabIndex="-1" aria-hidden="true" className="hidden modal__box">
            <div className="modal__container">
                <div className="modal__inner-container">
                    <ModalHeader title={$t('Delete Recruitment Status')} close={onClose} />
                    <div className="tw-rounded-b">
                        <div className="modal__form-container">
                            <div className="tw-flex tw-flex-col tw-gap-y-4 tw-text-sm">
                                {status && !needsTarget && <p>{$t('Are you sure you want to delete "{{name}}"?', { name: status.name })}</p>}
                                {status && needsTarget && (
                                    <>
                                        <p>
                                            {status.positions_using > 0
                                                ? $t('{{count}} positions use "{{name}}". Choose the status to move them to before deleting it.', {
                                                      count: status.positions_using,
                                                      name: status.name,
                                                  })
                                                : $t(
                                                      'Positions in other budget versions use "{{name}}". Choose the status to move them to before deleting it.',
                                                      {
                                                          name: status.name,
                                                      },
                                                  )}
                                        </p>
                                        <label className="tw-flex tw-flex-col tw-space-y-1.5">
                                            <span className="text-input__label">{$t('Move those positions to')} *</span>
                                            <select
                                                className="select select-input !tw-bg-white !tw-border"
                                                value={target}
                                                onChange={(e) => {
                                                    setTarget(e.target.value);
                                                    setError(null);
                                                }}
                                            >
                                                <option value="" disabled>
                                                    {$t('Select a status')}
                                                </option>
                                                <option value={NOT_SET}>{$t('Not set')}</option>
                                                {targets.map((item) => (
                                                    <option key={item.id} value={item.id}>
                                                        {item.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </>
                                )}
                                {error && <div className="tw-text-red-500 tw-text-sm">{error}</div>}
                            </div>
                            <ModalFooter
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Delete')}
                                onCancel={onClose}
                                handleSubmit={remove}
                                isSubmitting={isSubmitting}
                                primaryButtonDisabled={needsTarget && target === ''}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
